function makeHtmlMask(text: string) {
  const mask = new Array(text.length).fill(0);
  let inside = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (char === "<") {
      inside = true;
      mask[i] = 1;
    } else if (char === ">") {
      mask[i] = 1;
      inside = false;
    } else if (inside) {
      mask[i] = 1;
    }
  }

  return mask;
}

function isOutsideHtml(mask: number[], start: number, end: number) {
  for (let i = start; i < end; i++) {
    if (mask[i] === 1) return false;
  }
  return true;
}

const STOPWORDS = new Set([
  "a",
  "o",
  "as",
  "os",
  "de",
  "da",
  "do",
  "das",
  "dos",
  "em",
  "no",
  "na",
  "nos",
  "nas",
  "para",
  "por",
  "com",
  "sem",
  "e",
  "ou",
  "mas",
  "um",
  "uma",
]);

function findMatches(
  normalizedText: string,
  term: string,
  allowPartialMatch: boolean,
) {
  const matches: number[] = [];
  let index = 0;

  while (true) {
    const found = normalizedText.indexOf(term, index);
    if (found === -1) break;

    if (allowPartialMatch) {
      matches.push(found);
    } else {
      const before = found === 0 ? "" : normalizedText[found - 1];
      const after =
        found + term.length >= normalizedText.length
          ? ""
          : normalizedText[found + term.length];

      const isWordChar = (c: string | undefined) => /[a-z0-9]/i.test(c || "");

      if (!isWordChar(before) && !isWordChar(after)) {
        matches.push(found);
      }
    }

    index = found + term.length;
  }

  return matches;
}

export function getHighlightedExcerpts(
  text: string,
  searchTerm?: string,
  contextSize = 70,
  returnNullIfNotFound = true,
  allowPartialMatch = false,
) {
  if (!searchTerm) return [text.slice(0, 500)];

  const normalize = (str: string) =>
    str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const normalizedText = normalize(text);

  const quotedMatches = searchTerm.match(/"(.*?)"/g) || [];
  const quotedPhrases = quotedMatches.map((q) => ({
    raw: q.replace(/"/g, ""),
    normalized: normalize(q.replace(/"/g, "")),
  }));

  const restQuery = searchTerm
    .replace(/"(.*?)"/g, "")
    .trim()
    .split(/\s+/)
    .filter((w) => w && !STOPWORDS.has(w.toLowerCase()));

  let otherTerms: string[] = [];

  if (restQuery.length > 0) {
    otherTerms = normalize(restQuery.join(" "))
      .split(/\s+/)
      .filter((w) => w && !STOPWORDS.has(w));
  }

  let baseIndex: number;

  if (quotedPhrases.length > 0) {
    const firstPhrase = quotedPhrases[0];

    if (firstPhrase) {
      const [match] = findMatches(
        normalizedText,
        firstPhrase.normalized,
        allowPartialMatch,
      );

      baseIndex = match ?? -1;
    } else {
      baseIndex = -1;
    }

    if (baseIndex === -1) {
      for (const phrase of quotedPhrases) {
        const [match] = findMatches(
          normalizedText,
          phrase.normalized,
          allowPartialMatch,
        );

        if (match !== undefined) {
          baseIndex = match;
          break;
        }
      }

      if (baseIndex === -1) {
        quotedPhrases.length = 0;
      }
    }
  } else {
    baseIndex = Infinity;

    for (const w of otherTerms) {
      const [match] = findMatches(normalizedText, w, allowPartialMatch);

      if (match !== undefined) {
        baseIndex = Math.min(baseIndex, match);
      }
    }

    if (baseIndex === Infinity) {
      if (returnNullIfNotFound) return null;
      return [text];
    }
  }

  const start = Math.max(0, baseIndex - contextSize);
  const end = Math.min(text.length, baseIndex + contextSize * 3);

  let excerpt = text.slice(start, end);
  const normalizedExcerpt = normalize(excerpt);
  const mask = makeHtmlMask(excerpt);

  const ranges: { start: number; end: number }[] = [];

  for (const phrase of quotedPhrases) {
    const matches = findMatches(
      normalizedExcerpt,
      phrase.normalized,
      allowPartialMatch,
    );

    for (const found of matches) {
      const realEnd = found + phrase.raw.length;

      if (isOutsideHtml(mask, found, realEnd)) {
        ranges.push({
          start: found,
          end: realEnd,
        });
      }
    }
  }

  for (const term of otherTerms) {
    const matches = findMatches(normalizedExcerpt, term, allowPartialMatch);

    for (const found of matches) {
      const realEnd = found + term.length;

      if (isOutsideHtml(mask, found, realEnd)) {
        ranges.push({
          start: found,
          end: realEnd,
        });
      }
    }
  }

  if (ranges.length > 0) {
    ranges.sort((a, b) => a.start - b.start);

    const merged: { start: number; end: number }[] = [];

    for (const r of ranges) {
      const last = merged[merged.length - 1];

      if (!last || r.start > last.end) {
        merged.push({ ...r });
      } else {
        last.end = Math.max(last.end, r.end);
      }
    }

    let final = "";
    let lastIdx = 0;

    for (const r of merged) {
      final += excerpt.slice(lastIdx, r.start);
      final += `<mark style="background-color:#f0eee6;font-weight:700;padding:0;">${excerpt.slice(
        r.start,
        r.end,
      )}</mark>`;
      lastIdx = r.end;
    }

    final += excerpt.slice(lastIdx);
    excerpt = final;
  }

  return [(start > 0 ? "…" : "") + excerpt + (end < text.length ? "…" : "")];
}

export function highlightFullText(
  text: string,
  searchTerm?: string,
  allowPartialMatch = false,
): string {
  if (!searchTerm || !text) return text;

  const normalize = (str: string) =>
    str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const quotedMatches = searchTerm.match(/"(.*?)"/g) || [];
  const quotedPhrases = quotedMatches.map((q) => ({
    raw: q.replace(/"/g, ""),
    normalized: normalize(q.replace(/"/g, "")),
  }));

  const restQuery = searchTerm
    .replace(/"(.*?)"/g, "")
    .trim()
    .split(/\s+/)
    .filter((w) => w && !STOPWORDS.has(w.toLowerCase()));

  let otherTerms: string[] = [];

  if (restQuery.length > 0) {
    otherTerms = normalize(restQuery.join(" "))
      .split(/\s+/)
      .filter((w) => w && !STOPWORDS.has(w));
  }

  const normalizedText = normalize(text);
  const mask = makeHtmlMask(text);

  const ranges: { start: number; end: number }[] = [];

  if (quotedPhrases.length > 0) {
    for (const phrase of quotedPhrases) {
      const matches = findMatches(
        normalizedText,
        phrase.normalized,
        allowPartialMatch,
      );

      for (const found of matches) {
        const end = found + phrase.raw.length;

        if (isOutsideHtml(mask, found, end)) {
          ranges.push({ start: found, end });
        }
      }
    }
  }

  for (const term of otherTerms) {
    const matches = findMatches(normalizedText, term, allowPartialMatch);

    for (const found of matches) {
      const end = found + term.length;

      if (isOutsideHtml(mask, found, end)) {
        ranges.push({ start: found, end });
      }
    }
  }

  if (ranges.length === 0) return text;

  ranges.sort((a, b) => a.start - b.start);

  const merged: { start: number; end: number }[] = [];

  for (const r of ranges) {
    const last = merged[merged.length - 1];

    if (!last || r.start > last.end) {
      merged.push({ ...r });
    } else {
      last.end = Math.max(last.end, r.end);
    }
  }

  let result = "";
  let lastIndex = 0;

  for (const r of merged) {
    result += text.slice(lastIndex, r.start);
    result += `<mark style="background-color:#f0eee6;font-weight:700;padding:0;">${text.slice(
      r.start,
      r.end,
    )}</mark>`;
    lastIndex = r.end;
  }

  result += text.slice(lastIndex);

  return result;
}
